
import type { PropType } from 'vue'
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer'
import { defineComponent } from 'vue'

import { AlignEnum, PositionEnum } from '../enums'

import { EpbButton } from './shared/epb-button'

export default defineComponent({
  name: 'FadingScroll',
  components: {
    EpbButton,
  },
  props: {
    controls: {
      type: Boolean,
    },
    scrollFactor: {
      type: [Number, Object] as PropType<number | { controls: number; drag: number }>,
      default: () => ({ controls: 0.5, drag: 0.3 }),
    },
    resizeDirection: {
      type: String as PropType<AlignEnum>,
      default: AlignEnum.START,
    },
    disabled: {
      type: Boolean,
    },
  },
  data() {
    return {
      baseClass: 'epb-fading-scroll',
      scrollLeft: 0,
      scrollTop: 0,
      scrollWidth: 0,
      scrollHeight: 0,
      clientWidth: 0,
      clientHeight: 0,
      resizeObserver: undefined,
      mutationObserver: undefined,
    }
  },
  computed: {
    classes(): (string | Record<string, boolean>)[] {
      return [
        `${this.baseClass}`,
        {
          [`${this.baseClass}--vertical`]: this.scrollingY,
          [`${this.baseClass}--horizontal`]: this.scrollingX,
          [`${this.baseClass}--controls`]: this.controls,
        },
      ]
    },
    scrollingX(): boolean {
      return this.scrollWidth - this.clientWidth !== 0
    },
    scrollingY(): boolean {
      return this.scrollHeight - this.clientHeight !== 0
    },
    leftReached(): boolean {
      return this.scrollLeft === 0
    },
    rightReached(): boolean {
      return this.scrollLeft === this.scrollWidth - this.clientWidth
    },
    topReached(): boolean {
      return this.scrollTop === 0
    },
    bottomReached(): boolean {
      return this.scrollTop === this.scrollHeight - this.clientHeight
    },
    fadingOrientations(): PositionEnum[] {
      const fadingOrientations = []
      if (!this.leftReached) {
        fadingOrientations.push(PositionEnum.LEFT)
      }
      if (!this.rightReached) {
        fadingOrientations.push(PositionEnum.RIGHT)
      }
      if (!this.topReached) {
        fadingOrientations.push(PositionEnum.TOP)
      }
      if (!this.bottomReached) {
        fadingOrientations.push(PositionEnum.BOTTOM)
      }
      return fadingOrientations
    },
  },
  mounted(): void {
    const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill
    const MutationObserver = window.MutationObserver
    const mutationObserverConfig = { attributes: true, childList: true, subtree: true }
    this.resizeObserver = new ResizeObserver(this.updateScrollParams)
    this.mutationObserver = new MutationObserver(this.updateScrollParams)
    this.resizeObserver.observe(this.$el)
    this.mutationObserver.observe(this.$el, mutationObserverConfig)
  },
  beforeDestroy(): void {
    this.resizeObserver.disconnect()
    this.mutationObserver.disconnect()
  },
  methods: {
    updateScrollParams(): void {
      if (!this.$refs.content) {
        return
      }
      const prevWidth = this.scrollWidth
      const prevHeight = this.scrollHeight
      ;({
        scrollLeft: this.scrollLeft,
        scrollTop: this.scrollTop,
        scrollWidth: this.scrollWidth,
        scrollHeight: this.scrollHeight,
        clientWidth: this.clientWidth,
        clientHeight: this.clientHeight,
      } = this.$refs.content)

      const deltaWidth = this.scrollWidth - prevWidth
      const deltaHeight = this.scrollHeight - prevHeight

      if (deltaWidth || deltaHeight) {
        ;({
          [AlignEnum.START]: () => undefined,
          [AlignEnum.CENTER]: () => {
            this.scrollBy({ left: deltaWidth / 2, top: deltaHeight / 2 }, 'auto')
          },
          [AlignEnum.END]: () => {
            if (deltaWidth > 0) {
              this.scrollBy({ left: deltaWidth, top: deltaHeight }, 'auto')
            }
          },
        })[this.resizeDirection]()
      }
    },
    getControlIcon(orientation: PositionEnum): string {
      return orientation === PositionEnum.LEFT || orientation === PositionEnum.TOP
        ? 'chevronleftbig'
        : 'chevronrightbig'
    },
    scrollByFactor(orientation: PositionEnum, factor: number): void {
      const isHorizontalOrientation =
        orientation === PositionEnum.LEFT || orientation === PositionEnum.RIGHT
      const elementSize = isHorizontalOrientation ? this.clientWidth : this.clientHeight
      let scrollDistance = elementSize * factor
      if (orientation === PositionEnum.TOP || orientation === PositionEnum.LEFT) {
        scrollDistance *= -1
      }
      this.scrollBy(isHorizontalOrientation ? { left: scrollDistance } : { top: scrollDistance })
    },
    scrollBy(options: { left?: number; top?: number }, behavior = 'smooth'): void {
      this.$refs.content.scrollBy({
        ...options,
        behavior,
      })
    },
    scrollTo(options: { left?: number; top?: number }, behavior = 'smooth'): void {
      this.$refs.content.scrollTo({
        ...options,
        behavior,
      })
    },
  },
})
